import type { PanelTest } from "@skyware-group/iris-api-model";

import { BaseClient } from "../../clients/base/BaseClient";
import type { Paged, PaginationParams } from "../_shared/pagination.types";
import { PANEL_TESTS_ENDPOINTS } from "./panel-tests.consts";
import type { PanelTestCreateRequest, PanelTestUpdateRequest } from "./panel-tests.types";

/**
 * Panel Tests API Client
 * Manages tests included in examination panels.
 */
export class PanelTestsClient extends BaseClient {
  /**
   * Fetch a page of tests in a panel.
   * @param instrumentId - Instrument ID.
   * @param panelId - Panel ID.
   * @param params - Optional pagination parameters.
   * @param signal - Optional abort signal.
   */
  async fetchPanelTests(
    instrumentId: number,
    panelId: number,
    params?: PaginationParams,
    signal?: AbortSignal | null,
  ): Promise<Paged<PanelTest>> {
    return this.get<Paged<PanelTest>>(PANEL_TESTS_ENDPOINTS.PANEL_TESTS(instrumentId, panelId), {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  /**
   * Add a test to a panel.
   * @param instrumentId - Instrument ID.
   * @param panelId - Panel ID.
   * @param data - Panel test create request.
   * @param signal - Optional abort signal.
   */
  async createPanelTest(
    instrumentId: number,
    panelId: number,
    data: PanelTestCreateRequest,
    signal?: AbortSignal | null,
  ): Promise<PanelTest> {
    return this.post<PanelTest>(PANEL_TESTS_ENDPOINTS.CREATE_PANEL_TEST(instrumentId, panelId), {
      body: data,
      signal,
    });
  }

  /**
   * Update includeWhenFixed / includeWhenJoined for a panel-test.
   * @param instrumentId - Instrument ID.
   * @param panelId - Panel ID.
   * @param testId - Test ID to update.
   * @param data - Panel-test update payload.
   * @param signal - Optional abort signal.
   */
  async updatePanelTest(
    instrumentId: number,
    panelId: number,
    testId: number,
    data: PanelTestUpdateRequest,
    signal?: AbortSignal | null,
  ): Promise<PanelTest> {
    return this.put<PanelTest>(PANEL_TESTS_ENDPOINTS.UPDATE_PANEL_TEST(instrumentId, panelId, testId), {
      body: data,
      signal,
    });
  }

  /**
   * Remove a test from a panel.
   * @param instrumentId - Instrument ID.
   * @param panelId - Panel ID.
   * @param testId - Test ID to remove.
   * @param signal - Optional abort signal.
   */
  async deletePanelTest(
    instrumentId: number,
    panelId: number,
    testId: number,
    signal?: AbortSignal | null,
  ): Promise<void> {
    return this.delete<never>(PANEL_TESTS_ENDPOINTS.DELETE_PANEL_TEST(instrumentId, panelId, testId), { signal });
  }
}
